import React, { useState, useCallback, useRef, useLayoutEffect } from 'react';
import { InvoiceLineItem, formatCurrency, isUnpricedLineType } from '../../config';
import { LINE_ITEM_INDENT_PX } from '../../invoiceLayout';

interface LineItemEditorProps {
  item: InvoiceLineItem;
  index: number;
  onChange: (updates: Partial<InvoiceLineItem>) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onInsertAfter?: (type: string) => void;
  onSlashCommand?: (position: { top: number; left: number }) => void;
  isSlashMenuOpen?: boolean;
  onEnterOnEmpty?: () => void;
  onBackspaceOnEmpty?: () => void;
  /** A blank line sits above this row (either neighbour asked for it). */
  gapAbove?: boolean;
  /** This is the focused line, so its row controls stay visible. */
  isActive?: boolean;
  onRowFocus?: (index: number, rowElement: HTMLTableRowElement) => void;
  /** When this line is the "Less previous claims" deduction, the editor can
   *  pass a recompute target so the user can reset the amount to the
   *  context-computed sum after a manual override. */
  lessClaimsRecomputeTarget?: number | null;
  /** When true, render the row in read-only mode: contenteditable is disabled
   *  (typing into the description is blocked, not just mouse interaction),
   *  recompute button is hidden. CSS still handles input/button neutering. */
  readOnly?: boolean;
}

function fmtCurrency(val: number | null): string {
  if (val == null) return '';
  return formatCurrency(val);
}

const LineItemEditor: React.FC<LineItemEditorProps> = ({
  item,
  index,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  onInsertAfter,
  onSlashCommand,
  isSlashMenuOpen,
  onEnterOnEmpty,
  onBackspaceOnEmpty,
  gapAbove,
  isActive,
  onRowFocus,
  lessClaimsRecomputeTarget,
  readOnly = false,
}) => {
  const rowRef = useRef<HTMLTableRowElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  // A drawing line's two other cells; its title is the description cell.
  const drawingNumberRef = useRef<HTMLDivElement>(null);
  const issueNumberRef = useRef<HTMLDivElement>(null);
  const isTextLine = isUnpricedLineType(item.line_type) || item.line_type === 'phase_label';
  const isDrawing = item.line_type === 'drawing';

  // Layout, not passive: the row's text is written imperatively (the cell is
  // contentEditable, so React cannot own its children), and InvoiceEditor
  // measures every row in its own layout effect to decide where the page
  // breaks. Child layout effects run before the parent's, so this puts the
  // text in the DOM in time to be measured. As a passive effect it did not:
  // moving a row to another page remounts it, the parent then measured an
  // empty cell, paginated it back, remounted it again -- the measure loop
  // never settled and froze with a row hanging past the bottom margin.
  useLayoutEffect(() => {
    const el = descriptionRef.current;
    if (el && el.textContent !== item.description) {
      el.textContent = item.description;
    }
  }, [item.description]);

  // The drawing cells are written the same way, for the same reason.
  useLayoutEffect(() => {
    const pairs: Array<[HTMLDivElement | null, string]> = [
      [drawingNumberRef.current, item.drawing_number ?? ''],
      [issueNumberRef.current, item.issue_number ?? ''],
    ];
    for (const [el, value] of pairs) {
      if (el && el.textContent !== value) el.textContent = value;
    }
  }, [item.drawing_number, item.issue_number, isDrawing]);

  // A number too long for its column runs on over the issue (on both
  // surfaces; see DRAWING_NUMBER_MM). Say so here, while it can be fixed,
  // rather than leave it to be noticed on the printed invoice. Editor-only
  // marks that take no space, so the measured row is unchanged.
  useLayoutEffect(() => {
    const el = drawingNumberRef.current;
    if (!el) return;
    const overflowing = el.scrollWidth > el.clientWidth + 1;
    el.classList.toggle('is-overflowing', overflowing);
    if (overflowing) el.title = 'Too long for its column: it will print over the issue.';
    else el.removeAttribute('title');
  }, [item.drawing_number, isDrawing]);

  const handleRowFocus = useCallback(() => {
    if (onRowFocus && rowRef.current) {
      onRowFocus(index, rowRef.current);
    }
  }, [onRowFocus, index]);
  const handleContentEditableInput = useCallback(
    (e: React.FormEvent<HTMLDivElement>) => {
      onChange({ description: e.currentTarget.textContent || '' });
    },
    [onChange]
  );

  const handleQuantityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value === '' ? null : parseFloat(e.target.value);
      onChange({ quantity: val });
    },
    [onChange]
  );

  // Rate: editable text input with formatting on blur
  const [rateEditing, setRateEditing] = useState(false);
  const [rateText, setRateText] = useState('');
  const rateRef = useRef<HTMLInputElement>(null);

  const handleRateFocus = useCallback(() => {
    setRateEditing(true);
    setRateText(item.rate != null ? item.rate.toString() : '');
  }, [item.rate]);

  const handleRateBlur = useCallback(() => {
    setRateEditing(false);
    const val = rateText === '' ? null : parseFloat(rateText);
    if (!isNaN(val as number)) {
      onChange({ rate: val });
    }
  }, [rateText, onChange]);

  // Commit as you type, not only on blur: Escape closes the editor without
  // blurring, which used to discard a freshly typed rate entirely. Blur still
  // commits (and reformats), so the two paths agree.
  const handleRateTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setRateText(text);
    const val = parseFloat(text);
    if (!isNaN(val)) {
      onChange({ rate: val });
    }
  }, [onChange]);

  // Amount: editable text input with formatting on blur
  const [amountEditing, setAmountEditing] = useState(false);
  const [amountText, setAmountText] = useState('');

  const handleAmountFocus = useCallback(() => {
    setAmountEditing(true);
    setAmountText(item.amount != null ? item.amount.toString() : '');
  }, [item.amount]);

  const handleAmountBlur = useCallback(() => {
    setAmountEditing(false);
    const val = amountText === '' ? null : parseFloat(amountText);
    if (!isNaN(val as number)) {
      onChange({ amount: val });
    }
  }, [amountText, onChange]);

  // Commit as you type; see the rate field above.
  const handleAmountTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setAmountText(text);
    const val = parseFloat(text);
    if (!isNaN(val)) {
      onChange({ amount: val });
    }
  }, [onChange]);

  // Handle Enter key: for bullet/text lines, insert a new one after; for number fields, blur
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    }
  }, []);

  const handleDescriptionKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    // A drawing line is empty only when all three of its cells are.
    const isEmpty = !item.description.trim()
      && (!isDrawing || (!item.drawing_number?.trim() && !item.issue_number?.trim()));

    // While the slash menu is open, Enter picks its option (the menu listens on
    // the document). Acting on it here as well ran the line's own Enter too:
    // an empty line became a spacer with a new fee item under it, alongside
    // whatever the menu did.
    if (e.key === 'Enter' && isSlashMenuOpen) return;

    if (e.key === 'Enter') {
      e.preventDefault();
      if (isEmpty && onEnterOnEmpty) {
        onEnterOnEmpty();
      } else if (onInsertAfter) {
        // A run of drawings continues as drawings, as a run of bullets does.
        const nextType = (item.line_type === 'bullet' || item.line_type === 'text' || isDrawing) ? item.line_type : 'item';
        onInsertAfter(nextType);
      }
    }

    if (e.key === 'Backspace' && isEmpty && onBackspaceOnEmpty) {
      e.preventDefault();
      onBackspaceOnEmpty();
    }

    // Only from the description (a drawing's title): the number and issue
    // cells share this handler, and "/" is a character a drawing number or
    // issue can start with.
    if (e.key === '/' && isEmpty && !isSlashMenuOpen && onSlashCommand && e.currentTarget === descriptionRef.current) {
      e.preventDefault();
      const rect = descriptionRef.current?.getBoundingClientRect();
      const container = descriptionRef.current?.closest('.invoice-preview');
      const containerRect = container?.getBoundingClientRect();
      if (rect && containerRect) {
        onSlashCommand({
          top: rect.bottom - containerRect.top + 4,
          left: rect.left - containerRect.left,
        });
      }
    }
  }, [item.line_type, item.description, item.drawing_number, item.issue_number, isDrawing, onInsertAfter, onSlashCommand, isSlashMenuOpen, onEnterOnEmpty, onBackspaceOnEmpty]);

  const isDeduction = item.line_type === 'deduction' || item.line_type === 'prior_claims_deduction';
  // Reserved line type, plus legacy fallback for invoices created before the type existed
  const isLessClaims =
    item.line_type === 'prior_claims_deduction' ||
    (item.line_type === 'deduction' && item.description.trim() === 'Less previous claims');
  const isBullet = item.line_type === 'bullet';
  const isHeader = item.line_type === 'header';
  const isSpacer = item.line_type === 'spacer';

  const indentPx = (item.indent_level ?? 0) * LINE_ITEM_INDENT_PX;

  if (isSpacer) {
    return (
      <tr ref={rowRef} className={`line-item-row line-item-row--spacer${gapAbove ? ' line-item-row--gap-above' : ''}${isActive ? ' line-item-row--active' : ''}`} onFocus={handleRowFocus} tabIndex={-1}>
        <td className="col-description">&nbsp;</td>
        <td className="col-qty"></td>
        <td className="col-rate"></td>
        <td className="col-amount"></td>
        <td className="col-actions">
          <div className="action-buttons">
            {onMoveUp && (
              <button className="action-btn" onClick={onMoveUp} title="Move up">↑</button>
            )}
            {onMoveDown && (
              <button className="action-btn" onClick={onMoveDown} title="Move down">↓</button>
            )}
            <button className="action-btn action-btn--delete" onClick={onRemove} title="Remove">×</button>
          </div>
        </td>
      </tr>
    );
  }

  const placeholder = (() => {
    if (isHeader) return "Type '/' for commands or section heading...";
    if (isDrawing) return 'Title';
    if (isBullet || item.line_type === 'text' || item.line_type === 'phase_label') return "Type '/' for commands or text...";
    return "Type '/' for commands or item description...";
  })();

  return (
    <tr ref={rowRef} className={`line-item-row line-item-row--${item.line_type}${isLessClaims ? ' line-item-row--less-claims' : ''}${gapAbove ? ' line-item-row--gap-above' : ''}${isActive ? ' line-item-row--active' : ''}`} onFocus={handleRowFocus}>
      <td className="col-description">
        <div className={`description-cell${isDrawing ? ' description-cell--drawing' : ''}`} style={indentPx > 0 ? { paddingLeft: indentPx } : undefined}>
          {isBullet && <span className="bullet-prefix">-</span>}
          {isDrawing && (
            <>
              {/* Fixed-width columns, so a run of drawings reads as a table;
                  InvoicePDF draws the same widths (invoiceLayout.ts). The
                  number cell comes first in the DOM, so a new drawing line is
                  focused on its number. */}
              <span className="drawing-line__mark" contentEditable={false}>-</span>
              <div
                ref={drawingNumberRef}
                contentEditable={!readOnly}
                suppressContentEditableWarning
                onInput={readOnly ? undefined : (e) => onChange({ drawing_number: e.currentTarget.textContent || '' })}
                onKeyDown={readOnly ? undefined : handleDescriptionKeyDown}
                data-placeholder="Drawing no."
                className="description-input description-input--editable drawing-line__number"
              />
              <span className="drawing-line__issue">
                {/* The label is chrome and the issue is the data, so they are
                    two elements: a label inside the editable text would be
                    saved as part of the value. */}
                <span className="drawing-line__issue-label">Issue</span>
                <div
                  ref={issueNumberRef}
                  contentEditable={!readOnly}
                  suppressContentEditableWarning
                  onInput={readOnly ? undefined : (e) => onChange({ issue_number: e.currentTarget.textContent || '' })}
                  onKeyDown={readOnly ? undefined : handleDescriptionKeyDown}
                  data-placeholder="–"
                  className="description-input--editable drawing-line__issue-value"
                />
              </span>
            </>
          )}
          <div
            ref={descriptionRef}
            contentEditable={!readOnly}
            suppressContentEditableWarning
            onInput={readOnly ? undefined : handleContentEditableInput}
            onKeyDown={readOnly ? undefined : handleDescriptionKeyDown}
            data-placeholder={placeholder}
            className={`description-input description-input--editable${isHeader ? ' description-input--header' : ''}${isDrawing ? ' drawing-line__title' : ''}`}
          />
        </div>
      </td>
      <td className="col-qty">
        {!isTextLine && !isLessClaims && (
          <input
            type="number"
            value={item.quantity ?? ''}
            onChange={handleQuantityChange}
            placeholder="0"
            className="number-input"
            step="0.5"
            min="0"
          />
        )}
      </td>
      <td className="col-rate">
        {!isTextLine && !isLessClaims && (
          item.line_type === 'phase' ? (
            <span className="number-input number-input--readonly" title="Set by the project's fee plan">
              {fmtCurrency(item.rate)}
            </span>
          ) : (
            <input
              ref={rateRef}
              type="text"
              inputMode="decimal"
              value={rateEditing ? rateText : fmtCurrency(item.rate)}
              onChange={handleRateTextChange}
              onFocus={handleRateFocus}
              onBlur={handleRateBlur}
              onKeyDown={handleKeyDown}
              placeholder="0.00"
              className="number-input"
            />
          )
        )}
      </td>
      <td className={`col-amount ${isDeduction && !isLessClaims ? 'col-amount--deduction' : ''}`}>
        {!isTextLine && (
          item.line_type === 'phase' ? (
            <span className="number-input amount-input number-input--readonly" title="Auto-calculated from qty × rate">
              {fmtCurrency(item.amount)}
            </span>
          ) : isLessClaims ? (
            <input
              type="text"
              inputMode="decimal"
              value={amountEditing ? amountText : (item.amount != null ? `-(${fmtCurrency(Math.abs(item.amount))})` : '')}
              onChange={handleAmountTextChange}
              onFocus={handleAmountFocus}
              onBlur={handleAmountBlur}
              onKeyDown={handleKeyDown}
              placeholder="0.00"
              className="number-input amount-input"
              title="Sum of prior invoiced totals — edit to override"
            />
          ) : (
            <input
              type="text"
              inputMode="decimal"
              value={amountEditing ? amountText : fmtCurrency(item.amount)}
              onChange={handleAmountTextChange}
              onFocus={handleAmountFocus}
              onBlur={handleAmountBlur}
              onKeyDown={handleKeyDown}
              placeholder="0.00"
              className="number-input amount-input"
            />
          )
        )}
      </td>
      <td className="col-actions">
        <div className="action-buttons">
          {isLessClaims && lessClaimsRecomputeTarget != null && (
            <button
              type="button"
              className="action-btn"
              onClick={() => onChange({ amount: lessClaimsRecomputeTarget, rate: lessClaimsRecomputeTarget, quantity: 1 })}
              title="Recompute from prior invoice totals"
            >
              ↻
            </button>
          )}
          {onMoveUp && (
            <button className="action-btn" onClick={onMoveUp} title="Move up">
              ↑
            </button>
          )}
          {onMoveDown && (
            <button className="action-btn" onClick={onMoveDown} title="Move down">
              ↓
            </button>
          )}
          <button className="action-btn action-btn--delete" onClick={onRemove} title="Remove">
            ×
          </button>
        </div>
      </td>
    </tr>
  );
};

export default LineItemEditor;
